import {
  getV1CompaniesCompanyIdTelematicsFleetsFleetIdRemoteVehicles,
  patchV1CompaniesCompanyIdTelematicsRemoteVehiclesId,
  TelematicsRemoteVehicle_Update,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback } from 'react';

import connection from '~services/connectionModule';
import { extractPagination, PaginationLink } from '~services/pagination';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { TelematicsVehicle } from './models';

interface GetTelematicsVehicleByFleetIdParams {
  cursor?: string;
  fleetId: string;
  limit: number;
  link?: PaginationLink;
  searchQuery?: string;
  signal?: AbortSignal;
}

export default function useTelematicsVehicles() {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  const getTelematicsVehicleByFleetId = useCallback(
    async ({
      cursor,
      fleetId,
      limit,
      link,
      searchQuery,
      signal,
    }: GetTelematicsVehicleByFleetIdParams) => {
      try {
        const response =
          await getV1CompaniesCompanyIdTelematicsFleetsFleetIdRemoteVehicles({
            path: {
              'company-id': companyId,
              'fleet-id': fleetId,
            },
            query: {
              'page[limit]': limit,
              ...(searchQuery ? { 'search[datagrid]': searchQuery } : {}),
              ...(link && cursor ? { [`page[${link}]`]: cursor } : {}),
            },
            signal,
          });

        return {
          data: response.data.data.map(TelematicsVehicle.parse),
          pagination: extractPagination(response),
        };
      } catch (error) {
        if (signal?.aborted) {
          throw error;
        }

        connection.handleRequestError(
          error,
          t('error_messages.telematics.failed_to_fetch_vehicles'),
        );
        // `connection` has no explicit type, so TS can't see the `never` return
        throw error;
      }
    },
    [companyId],
  );

  const updateTelematicsVehicleById = useCallback(
    async (id: string, data: Partial<TelematicsRemoteVehicle_Update>) => {
      try {
        await patchV1CompaniesCompanyIdTelematicsRemoteVehiclesId({
          path: { 'company-id': companyId, id },
          body: data,
        });
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.telematics.failed_to_update_vehicle'),
        );
        throw error;
      }
    },
    [companyId],
  );

  return { getTelematicsVehicleByFleetId, updateTelematicsVehicleById };
}
